import type { InputState } from './InputState';

/**
 * Every controllable input source.
 *
 * The keyboard uses A/D, the canvas uses pointer drag, the on-screen control
 * bar uses hold-to-spin buttons, and the gyroscope uses device tilt. They all
 * want to command the same board, so instead of each writing `input.omegaTarget`
 * and clobbering the last writer, they report here and this picks ONE winner.
 */
export type InputSource = 'drag' | 'gyro' | 'buttons' | 'keyboard';

/**
 * Resolution order. A drag in progress beats the gyroscope, which beats the
 * on-screen buttons, which beat the keyboard. The FIRST source in this list that
 * is active AND asking for a non-zero speed wins; if none is, the board coasts to
 * a stop (`omegaTarget` 0, which `updateOmega` eases toward).
 *
 * `forcedOmega` (the verification harness override) still beats everything,
 * because it is read ahead of `omegaTarget` inside `App.updateOmega`.
 */
const PRIORITY: readonly InputSource[] = ['drag', 'gyro', 'buttons', 'keyboard'];

/**
 * The one small piece of logic that lets several input devices coexist.
 *
 * Each source calls `set()` every physics step with what IT wants; `apply()`
 * writes the resolved winner into the shared `InputState.omegaTarget`, the same
 * field the game loop has always read. Adding a device never touches the game.
 */
export class InputRouter {
  private readonly requested = new Map<InputSource, number>();
  private readonly active = new Map<InputSource, boolean>();

  constructor(private readonly input: InputState) {
    for (const source of PRIORITY) {
      this.requested.set(source, 0);
      this.active.set(source, false);
    }
  }

  /** A source reports its desired angular velocity (rad/s) and whether it is live this step. */
  set(source: InputSource, omega: number, active: boolean): void {
    this.requested.set(source, omega);
    this.active.set(source, active);
  }

  /** Forget every source - used on window blur so the board does not drift. */
  clearAll(): void {
    for (const source of PRIORITY) {
      this.requested.set(source, 0);
      this.active.set(source, false);
    }
  }

  /** The winning angular velocity by priority, or 0 if nothing is asking. */
  resolve(): number {
    for (const source of PRIORITY) {
      if (this.active.get(source) !== true) continue;
      const omega = this.requested.get(source) ?? 0;
      if (omega !== 0) return omega;
    }
    return 0;
  }

  /** Write the resolved value into the shared input record. */
  apply(): void {
    this.input.omegaTarget = this.resolve();
  }
}
